/* eslint-disable react-hooks/exhaustive-deps  */
/* eslint-disable array-callback-return  */
import React, { useEffect, useState, useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Svg, Modal, ModalTypical, Button } from 'app/components';
import useUtils from 'app/hooks/useUtils';
import { useTranslation } from 'react-i18next';
import useSeatLegendEffect from '../hooks/useSeatLegendEffect';
import useSeatPlanEffect from '../hooks/useSeatPlanEffect';
import PlanBuilder from './components/PlanBuilder';
import SeatPlanLegend from './components/SeatPlanLegend';
import {
  BlockSeatPlanInner,
  SeatPlanHall,
  SeatPlanHallInner,
  ButtonZoom,
  LegendWrapper,
  ScreenLine,
  ListRows
} from './styles';
import { useSelector } from 'react-redux';
import { useLocation, useHistory, generatePath } from 'react-router-dom';
import useModal from 'app/hooks/useModal';
import useConfig from 'app/hooks/useConfig';

const propTypes = {
  orderRequest: PropTypes.func.isRequired,
  orderSessionRequest: PropTypes.func.isRequired,

  seatPlan: PropTypes.shape({
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    icons: PropTypes.arrayOf(PropTypes.object).isRequired,
    rowsMax: PropTypes.number.isRequired,
    error: PropTypes.string
  }),
  seatsLegend: PropTypes.arrayOf(PropTypes.object),
  order: PropTypes.shape({
    userSessionId: PropTypes.string.isRequired,
    orderTotalValueInCents: PropTypes.number.isRequired,
    tickets: PropTypes.arrayOf(PropTypes.object).isRequired
  }),
  sessionId: PropTypes.string.isRequired,
  cinemaId: PropTypes.string.isRequired
};

const defaultProps = {
  seatPlan: null,
  order: null,
  seatsLegend: null
};

const widthsMap = {
  xl: {
    zoomOutValue: 500,
    zoomInValue: 500 * 1.4
  },
  md: {
    zoomOutValue: 640,
    zoomInValue: Math.round(640 * 1.5)
  },
  sm: {
    zoomOutValue: 500,
    zoomInValue: Math.round(500 * 1.5)
  },
  xs: {
    zoomOutValue: 280,
    zoomInValue: Math.round(260 * 1.7)
  }
};

const SittingPlan = ({
  orderRequest,
  orderSessionRequest,
  seatPlan,
  order,
  seatsLegend,
  sessionId,
  cinemaId,
  notify
}) => {
  const [t] = useTranslation();
  const { buildContentStaticUrl, getWindowWidth } = useUtils();
  const userSessionId = order ? order.userSessionId : null;
  const [seatPlanWidth, setSeatPlanWidth] = useState(widthsMap[getWindowWidth()].zoomOutValue);
  const [planBuilder, initPlanBuilder] = useState(null);
  const [isZoomIn, toggleZoom] = useState(false);
  const location = useLocation();
  const email = location?.state?.email;
  const isAboId = location?.state?.isAboId;
  const aboId = location?.state?.aboId;

  const selectSeats = useCallback((seatsData) => {
    orderRequest(cinemaId, sessionId, seatsData, null, null, null, null, isAboId, aboId, showProfileUpdateModal);
  }, []);

  const seatPlanData = useSelector(state => state?.sessionsTickets?.plan);
  const [profileUpdate, showProfileUpdate, hideProfileUpdate] = useModal('profileUpdate');
  const history = useHistory();
  const {
    routes,
    country: countryCode
  } = useConfig();

  const showProfileUpdateModal = () => {
    if (countryCode === "rou") {
      showProfileUpdate();
    }
  };

  const onClickEditProfile = () => {
    hideProfileUpdate();
    history.push({
      pathname: generatePath(routes.userEdit.pattern),
    });
  };

  const doZoom = useCallback(
    e => {
      const width = getWindowWidth();
      const isEventResize = e.type === 'resize';
      const valueToSet = isEventResize
        ? widthsMap[width][isZoomIn ? 'zoomInValue' : 'zoomOutValue']
        : widthsMap[width][isZoomIn ? 'zoomOutValue' : 'zoomInValue'];

      if (valueToSet === seatPlanWidth) return;
      if (!isEventResize) toggleZoom(!isZoomIn);

      setSeatPlanWidth(valueToSet);
    },
    [isZoomIn, seatPlanWidth]
  );

  useEffect(() => {
    window.addEventListener('resize', doZoom);

    return () => window.removeEventListener('resize', doZoom);
  }, [doZoom]);

  useEffect(() => {
    if (seatPlanData && seatPlanData.rows && seatPlanData.icons && seatPlanData.rowsMax && !seatPlanData.error) {
      const plan = new PlanBuilder(notify, seatPlanData, selectSeats, seatPlanWidth, buildContentStaticUrl);
      initPlanBuilder(plan);
    }
    else if (seatPlan && seatPlan.rows && seatPlan.icons && seatPlan.rowsMax && !seatPlan.error) {
      const plan = new PlanBuilder(notify, seatPlan, selectSeats, seatPlanWidth, buildContentStaticUrl);
      initPlanBuilder(plan);
    }
  }, [order, seatPlanData, seatPlan, seatPlanWidth, buildContentStaticUrl]);

  useSeatLegendEffect(cinemaId, sessionId);
  useSeatPlanEffect(cinemaId, sessionId, userSessionId, !!planBuilder);

  return (
    <div className="b-seatplan">

      {countryCode === "rou" && (
        <>
          <Modal
            isOpen={profileUpdate}
            close={hideProfileUpdate}
            title={t('modals.profile_update.title')}
          >
            <ModalTypical
              iconId="clock-circled"
              heading={t("modals.profile_update.header")}
              message={t("modals.profile_update.message_1")}
            >
              <Button
                style={{ width: "200px" }}
                isPrimary
                onClick={onClickEditProfile}
              >
                {t("common.edit_profile")}
              </Button>
            </ModalTypical>
          </Modal>
        </>
      )}

      <BlockSeatPlanInner>
        {seatPlan && seatPlan.error ? <SeatPlanHall>{seatPlan.error}</SeatPlanHall> : null}
        {seatPlanData && seatPlanData.error ? <SeatPlanHall>{seatPlanData.error}</SeatPlanHall> : null}

        {planBuilder ? (
          <Fragment>
            <SeatPlanHall>
              <SeatPlanHallInner
                style={{
                  width: `${Math.round(planBuilder.rowLengthPx + planBuilder.rowLengthPx / 11.1)}px`
                }}
              >
                <ScreenLine width={planBuilder.rowLengthPx}>
                  <Svg id="screen" />
                  <p>{t('purchase.screen')}</p>
                </ScreenLine>
                <ListRows width={(planBuilder?.maxRowLength <= 13 && planBuilder.rowLengthPx >= 500) ? "400" : planBuilder.rowLengthPx}>
                  {planBuilder.getCompletedMarkup()}
                </ListRows>
              </SeatPlanHallInner>
            </SeatPlanHall>
            <div className="b-seatplan__btn-zoom-wrapper">
              <ButtonZoom onClick={doZoom}>
                {isZoomIn ? <Svg id="zoom-out" /> : <Svg id="zoom-in" />}
              </ButtonZoom>
            </div>
          </Fragment>
        ) : null}

        {seatsLegend && seatsLegend.length ? (
          <LegendWrapper>
            <SeatPlanLegend legendData={seatsLegend} />
          </LegendWrapper>
        ) : null}
      </BlockSeatPlanInner>
    </div>
  );
};

SittingPlan.propTypes = propTypes;
SittingPlan.defaultProps = defaultProps;
export default SittingPlan;
